import { enCommentStatus, IComments } from 'interface/comment';
import { Typography, Box, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { getCdnUrl, getTime } from 'utils';
import DefaultAvatar from 'assets/img/avatar.png';
import { CourseTheme } from 'assets/themes/Course';
import PrivateIcon from '@eduzz/houston-icons/LockedSolid';
import PublicIcon from '@material-ui/icons/Public';
import Tooltip from '@eduzz/houston-ui/Tooltip';
import { useAppSelector } from 'hooks/redux';
import { ChevronDown, ChevronUp } from '@eduzz/houston-icons';
import { useState, useEffect, useCallback } from 'react';
import MakeComment from '../MakeComment';
import { saveCommentReply, getReplies, deleteComment } from 'services/comments';
import { useParams } from 'react-router-dom';
import { enTypeComment } from 'interface/lessonComment';

const useStyles = makeStyles<CourseTheme>((theme) => ({
  container: {
    background: theme.variables.courseTheme.cardBackground,
    borderRadius: 8,
    margin: theme.spacing(4),
    padding: theme.spacing(4),
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(6, 0),
    '& *[data-disabled=true]': {
      opacity: 0.5,
      pointerEvents: 'none',
    },
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: '50%',
  },
  title: {
    color: theme.variables.courseTheme.text,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    fontWeight: 700,
  },
  description: {
    color: theme.variables.courseTheme.text,
  },
  createdAt: {
    marginLeft: theme.spacing(2),
    color: theme.palette.grey[500],
  },
  line: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(4),
  },
  icon: {
    color: theme.variables.courseTheme.text,
    marginLeft: theme.spacing(2),
  },
  buttonText: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    border: 0,
    borderRadius: 4,
    padding: 10,
    background: `${theme.palette.grey[500]} !important`,
    color: `${theme.palette.common.white} !important`,
    '&:hover, &:disabled': {
      background: `${theme.palette.grey[600]} !important`,
    },
    '& svg': {
      color: theme.variables.courseTheme.text,
    },
  },
}));

interface IProps extends IComments {
  isReply?: boolean;
  type: enTypeComment;
  resetComments?: Function;
  setReplies?: Function;
}

const CommentItem = ({ type, id, setReplies, replies, ...props }: IProps) => {
  const [enableMakeComment, setEnableMakeComment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReplies, setShowReplies] = useState(false);
  const [comments, setComments] = useState<IComments[]>([]);
  const canReply = useAppSelector((state) => state.newLesson.lesson.can_reply);
  const user = useAppSelector((state) => state.user);
  const classes = useStyles();
  const { lessonID } = useParams<{ lessonID: string }>();

  const toggleMakeComment = () => {
    setEnableMakeComment((isEnabled) => !isEnabled);
  };

  const toggleShowReplies = () => {
    setShowReplies((isShowing) => !isShowing);
  };

  const getCommentReplies = useCallback(() => {
    getReplies(parseInt(lessonID), type, id).then((replies) => {
      if (setReplies) setReplies(replies);
      setComments(replies);
    });
  }, [lessonID, type, id, setReplies]);

  const onSubmitReply = (lessonId: number, comment: string) => {
    return saveCommentReply(lessonId, id, comment).then(() => getCommentReplies());
  };

  const onDeleteComment = () => {
    if (loading) return;
    setLoading(true);
    deleteComment(parseInt(lessonID), id, props.isReply)
      .then(() => {
        if (props.isReply) {
          return getCommentReplies();
        }
        return props.resetComments();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (showReplies && comments.length === 0) {
      getCommentReplies();
    }
  }, [showReplies, comments, getCommentReplies]);

  return (
    <div className={classes.root}>
      <div className={classes.line}>
        <img className={classes.avatar} alt={props.user.name} src={getCdnUrl(props.user.avatar, DefaultAvatar)} />
        <Typography
          aria-label='Nome do usuário que fez o comentário'
          variant='body1'
          className={classes.title}
          id='lesson-comment-user-name'
        >
          {props.user.name}
        </Typography>
        <Typography
          aria-label='Data de criação do comentário'
          variant='body2'
          className={classes.createdAt}
          id='lesson-comment-time'
        >
          {getTime(props.createdAt)}
        </Typography>
        {props.status === enCommentStatus.public && (
          <Tooltip aria-label='Status do comentário: Público' title='Público' placement='right'>
            <PublicIcon className={classes.icon} />
          </Tooltip>
        )}
        {props.status === enCommentStatus.private && (
          <Tooltip aria-label='Status do comentário: Privado' title='Privado' placement='right'>
            <PrivateIcon className={classes.icon} size={16} />
          </Tooltip>
        )}
      </div>
      <Box marginY={4}>
        <Typography
          aria-label='Comentário do usuário'
          variant='body1'
          className={classes.description}
          component='div'
          dangerouslySetInnerHTML={{ __html: props.text }}
          id='lesson-comment-content'
        />
      </Box>
      <div className={classes.line}>
        {!props.isReply && (
          <>
            {(Boolean(props.totalReplies) || comments.length > 0) && (
              <Typography
                aria-label='Botão vizualizar todos os comentários'
                variant='body1'
                className={classes.buttonText}
                onClick={toggleShowReplies}
                id='lesson-comments-see-all'
                component='button'
              >
                Ver todos os comentários {!showReplies && <ChevronDown className={classes.icon} />}
                {showReplies && <ChevronUp className={classes.icon} />}
              </Typography>
            )}
            {canReply && props.user.id === user.id && (
              <Typography
                component='button'
                aria-label='Botão responder comentário'
                variant='body1'
                className={classes.buttonText}
                onClick={toggleMakeComment}
                id='lesson-comments-reply'
              >
                Responder
              </Typography>
            )}
          </>
        )}
        {props.user.id === user.id && (
          <Typography
            aria-label='Botão excluir comentário'
            data-disabled={loading}
            variant='body1'
            className={classes.buttonText}
            onClick={onDeleteComment}
            component='button'
            id='lesson-comments-delete'
          >
            {loading && (
              <Box marginRight={1}>
                <CircularProgress size={14} />
              </Box>
            )}
            Excluir
          </Typography>
        )}
      </div>
      {!props.isReply && enableMakeComment && (
        <Box margin={4} marginBottom={0}>
          <MakeComment onSubmit={onSubmitReply} />
        </Box>
      )}
      {showReplies && (
        <div className={classes.container}>
          {comments.map((comment) => (
            <CommentItem key={comment.id} {...comment} isReply={true} type={type} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CommentItem;
